// @ts-check
import gql from 'graphql-tag'
import _ from 'lodash'
import { allMutations, allQueries } from 'src/models/model/constants'
import Model from 'src/models/model/model'
import { isPrimitive, isSimpleType } from 'src/models/model/utils/model-utils'

/**
 * Builds a GQL query string.
 * @param {string} queryName - the name of the query
 * @param {Record<string, any>} variables - a hash that matches the definition, e.g. {id: 3}
 * @param {Record<string, any>} options
 * @example
 * {
 *   items: {
 *     fields: 'detail',
 *     itemVariations: {
 *       fields: ['id', 'name']
 *     },
 *     itemModifierSets: {
 *       defaultModifiers: 'default'
 *     },
 *     modifierSet: {
 *       modifiers: 'default'
 *     }
 *   },
 *   errors: 'default'
 * }
 */
export function buildQuery (queryName, variables, options) {
  const query = allQueries[queryName]
  if (!query) {
    throw new Error('Unknown query: ' + queryName)
  }

  let resultsQueryLines = []

  let paginating = false
  if (options.paginate) {
    paginating = true
  }
  let validatable = false
  if (options.validatable) {
    validatable = true
  }
  let subscriptionName
  if (options.subscriptionName) {
    subscriptionName = options.subscriptionName
  }
  let forceLanguage
  if (options.forceLanguage) {
    forceLanguage = options.forceLanguage
  }
  const isCollection = options.isCollection

  // Always one result returned for queries and it doesn't need to be named
  options = {
    [_.keys(query.results)[0]]: options
  }

  _.each(options, (options, name) => {
    let Model = query.results[name]

    if (!Model) {
      throw new Error(`Result type [${name}] not found on query ${queryName}`)
    }

    if (Array.isArray(Model)) {
      Model = Model[0]
    }

    buildResultsQuery(Model, options, 4, resultsQueryLines)
  })

  if (paginating) {
    if (validatable) {
      resultsQueryLines = ['  data {', ...resultsQueryLines, '  }', 'count', 'resultVersion']
    } else {
      resultsQueryLines = ['  data {', ...resultsQueryLines, '  }', 'count', 'isMore']
    }
  }

  if (Model.isTranslationEditingAllowed && isCollection) {
    resultsQueryLines.push('_lang')
  }

  if (subscriptionName) {
    resultsQueryLines = [`  ${queryName} {`, ...resultsQueryLines, '  }']
  }

  if (resultsQueryLines.length) {
    resultsQueryLines[0] = resultsQueryLines[0].trim()
  }

  let queryOrSubscription
  let queryOrSubscriptionName
  if (subscriptionName) {
    queryOrSubscription = 'subscription'
    queryOrSubscriptionName = subscriptionName
  } else {
    queryOrSubscription = 'query'
    queryOrSubscriptionName = queryName
  }

  let queryLevelDirectives
  if (forceLanguage) {
    queryLevelDirectives = {
      locale: { lang: forceLanguage }
    }
  }

  return (
    `${queryOrSubscription}${buildArgumentsHeader(queryName, query.arguments, variables)} ${buildDirectivesCall(queryLevelDirectives)} {
      ${queryOrSubscriptionName}${buildArgumentsCall(query.arguments, variables)} {
        ${resultsQueryLines.join('\n')}
      }
    }`
  )
}

/**
 * Executes a GQL mutation.
 * @param {string} name - the name of the mutation
 * @param {Record<string, any>} variables - a hash that matches the definition, e.g. {id: 3}
 * @param {Record<string, any>} options
 */
export async function executeMutation (name, variables, options, retryCount = 0) {
  try {
    const mutation = buildMutation(name, variables, options)
    const result = await Model.apollo.mutate({
      mutation,
      variables
    })

    const data = _.cloneDeep(result.data[name])

    // TODO unpack according to actual result structure! Don't assume errors
    const errors = data.errors || []

    return { data, errors, query: mutation?.loc?.source?.body, response: result }
  } catch (error) {
    console.log('executeMutation error')
    console.log(error)

    const shouldRetry = error?.networkError?.result?.error?.recoverable
    if (retryCount < 3 && shouldRetry) {
      // When the record we are attempting to mutate is locked, we should retry a few times
      // This is to improve the user experience by gracefully handling the situation
      // Previously this would be handled by the server, but we are now handling it on the client
      // This is to avoid the api thread from waiting for the lock to be released, instead deferring to the client
      await new Promise((resolve) => setTimeout(resolve, 1000))
      return await executeMutation(name, variables, options, retryCount + 1)
    }
    return {
      errors: [error]
    }
  }
}

/**
 * Builds a GQL mutation string.
 * @param {string} mutationName - the name of the mutation
 * @param {Record<string, any>} variables - a hash that matches the definition, e.g. {id: 3}
 * @param {Record<string, any>} options
 */
export function buildMutation (mutationName, variables, options) {
  const mutation = allMutations[mutationName]
  if (!mutation) {
    throw new Error('Unknown mutation: ' + mutationName)
  }

  const resultsQueryLines = []
  const indentCount = 5
  const indent = '  '.repeat(indentCount)
  let forceLanguage

  _.each(options, (options, name) => {
    let Model = mutation.results[name]

    if (!Model) {
      throw new Error('Result type ' + name + ' not found on mutation ' + mutationName)
    }

    if (Array.isArray(Model)) {
      Model = Model[0]
    }

    forceLanguage ||= options.forceLanguage

    if (isPrimitive(Model) || isSimpleType(Model)) {
      resultsQueryLines.push(indent + name)
    } else {
      resultsQueryLines.push(indent + name + ' {')
      buildResultsQuery(Model, options, indentCount + 1, resultsQueryLines)
      resultsQueryLines.push('}')
    }
  })

  if (resultsQueryLines.length) {
    resultsQueryLines[0] = resultsQueryLines[0].trim()
  }

  let mutationLevelDirectives
  if (forceLanguage) {
    mutationLevelDirectives = {
      locale: { lang: forceLanguage }
    }
  }

  // console.log('MUTATION')
  // console.log(`
  //   mutation${buildArgumentsHeader(mutationName, mutation.arguments, variables, true)} ${buildDirectivesCall(mutationLevelDirectives)}  {
  //     ${mutationName}${buildArgumentsCall(mutation.arguments, variables, true)} {
  //       ${resultsQueryLines.join('\n')}
  //     }
  //   }
  // `)

  return gql`
    mutation${buildArgumentsHeader(mutationName, mutation.arguments, variables, true)} ${buildDirectivesCall(mutationLevelDirectives)} {
      ${mutationName}${buildArgumentsCall(mutation.arguments, variables, true)} {
        ${resultsQueryLines.join('\n')}
      }
    }
  `
}

/**
 * Builds a list of GQL query results string lines for a model that will be concatenated to form the final GQL query string.
 * @param {Model} Model
 * @param {Record<string, any>} options
 * @param {number} indentCount
 * @param {string[]} resultsQueryLines
 * @returns {string[]} - the GQL query string lines
 * @example
 *
 *   item {
 *     id
 *     name
 *     abbreviation
 *     itemVariations {
 *       id
 *       name
 *       price {
 *         amount
 *       }
 *     }
 *   }
 */
export function buildResultsQuery (Model, options, indentCount, resultsQueryLines = []) {
  const indent = '  '.repeat(indentCount)

  // We don't do anything special for arrays as that information is encoded on the gql type.
  // We specify a query for an object and collection identically
  if (_.isArray(options.structure)) {
    options.structure = options.structure[0]
  }

  // Build query recursively for all submodels. Structure objects are key-value pairs of nested field names
  // mapping to a boolean or a function that returns a boolean. E.g. { structure: { itemVariations: true, ... } }
  _.each(options.structure, (shouldInclude, subfield) => {
    if (typeof shouldInclude === 'function') {
      // This is the case when a field.includeInQueryIf function is set.
      shouldInclude = shouldInclude(options)
    }
    if (!shouldInclude) return

    const { Type, field } = Model.fieldType(subfield)
    if (!field) {
      throw new Error(`Could not find submodel field [${subfield}] on [${Model.name}]`)
    }
    if (field.input || field.local) {
      return
    }
    if (!_.isEmpty(field.requiresPermission) && !Model.context.isPermitted(field.requiresPermission)) {
      return
    } else if (!_.isEmpty(field.requiresAnyPermission) && !Model.context.isAnyPermitted(field.requiresAnyPermission)) {
      return
    }

    if (!Type) {
      throw new Error(`Could not find type of field [${subfield}] on [${Model.name}]`)
    }

    if (isPrimitive(Type) || isSimpleType(Type)) {
      resultsQueryLines.push(indent + subfield)
    } else {
      resultsQueryLines.push(indent + subfield + ' {')
      const submodelOptions = { structure: options.structure[subfield] || {}, forceLanguage: options.forceLanguage }
      buildResultsQuery(Type, submodelOptions, indentCount + 1, resultsQueryLines)
      resultsQueryLines.push(indent + '}')
    }
  })

  return resultsQueryLines
}

/**
 * Builds a GQL arguments header string.
 * @param {string} queryOrMutationName - the name of the query or mutation
 * @param {Record<string, any>} args - the arguments
 * @param {Record<string, any>} variables - the variables
 * @param {boolean} mutation - whether this is a mutation
 * @returns {string} - the GQL arguments header string
 * @example
 *   "($url: String!)"
 */
export function buildArgumentsHeader (queryOrMutationName, args, variables, mutation = false) {
  if (!args || !_.keys(args).length || !variables || !_.keys(variables).length) {
    return ''
  }

  const headerParts = _.map(_.intersection(_.keys(args), _.keys(variables)), (name) => {
    const argument = args[name]
    if (!argument) {
      throw new Error(`Cannot find argument [${name}] for [${queryOrMutationName}]`)
    }

    let type = argument.type || argument
    let array = false
    if (_.isArray(type)) {
      type = type[0]
      array = true
    }
    const required = argument.required || false

    let typeSignature = `${type.name}${(mutation && !(isPrimitive(type) || isSimpleType(type))) ? 'Input' : ''}${required ? '!' : ''}`
    if (array) {
      typeSignature = `[${typeSignature}]${required ? '!' : ''}`
    }
    return `$${name}: ${typeSignature}`
  })

  if (headerParts.length === 0) {
    return ''
  }

  return `(${headerParts.join(', ')})`
}

/**
 * Builds a GQL arguments call string.
 * @param {Record<string, any>} args - the arguments
 * @param {Record<string, any>} variables - the variables
 * @param {boolean} mutation - whether this is a mutation
 * @returns {string} - the GQL arguments call string
 * @example
 *   "(url: $url)"
 */
export function buildArgumentsCall (args, variables, mutation = false) {
  if (!args || !_.keys(args).length || !variables || !_.keys(variables).length) {
    if (mutation) {
      return '(input: {})'
    } else {
      return ''
    }
  }

  const callParts = _.map(_.intersection(_.keys(args), _.keys(variables)), name => `${name}: $${name}`)

  if (callParts.length === 0) {
    return ''
  }

  if (mutation) {
    return `(input: {${callParts.join(', ')}})`
  } else {
    return `(${callParts.join(', ')})`
  }
}

/**
 * Returns a GraphQL directives string. Expects an object with directive names as keys
 * mapping to an object of directive arguments key-value pairs. For example, given:
 * @param {Record<string, any>} directiveArgsByName - the directive arguments
 * @example
 * ```js
 *  {
 *    locale: { lang: 'es' },
 *    otherDirective: { arg1: 'value1', arg2: 'value2'}
 *  }
 * ```
 * it returns the string:
 * ```js
 *  '@locale(lang: "es") @otherDirective(arg1: "value1", arg2: "value2")'
 * ```
 */
export function buildDirectivesCall (directiveArgsByName) {
  if (_.isEmpty(directiveArgsByName)) return ''
  const directivesStr = _.map(directiveArgsByName, (params, directiveName) => {
    if (_.isEmpty(params)) return `@${directiveName}`
    return `@${directiveName}(${_.map(params, (argValue, paramName) => `${paramName}: ${JSON.stringify(argValue)}`).join(', ')})`
  }).join(' ')
  return directivesStr
}
